import React from "react";
import { Settings } from "lucide-react";
import Button from "@/components/ui/Button";
import { getColorByHash } from "@/helper/utils";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import useCampaignStore from "../store/campaignStore";

interface CampaignDetailWrapperProps {
  children?: React.ReactNode;
}

const CampaignDetailWrapper: React.FC<CampaignDetailWrapperProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const campaign = useCampaignStore((state) => state.campaign);
  const location = useLocation();

  if (!campaign) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">
          {location.pathname === "/campaign/flight"
            ? "Flight Setup"
            : location.pathname === "/campaign/assets"
              ? "Assets Setup"
              : location.pathname === "/campaign/summary"
                ? "Campaign Summary"
                : "N.A"}
        </h1>
        <Button
          type="secondary"
          leadingIcon={<Settings size={16} />}
          onClick={() => {
            navigate(`/campaign?id=${campaign.campaign.id}`);
          }}
        >
          Edit Settings
        </Button>
      </div>

      <div className="rounded-lg mb-8">
        <h2 className="text-lg font-semibold mb-4">
          {campaign.teams.find((e) => e.team_type === "client")?.name || "N/A"}
        </h2>
        <h3 className="text-2xl font-semibold mb-6">
          {campaign.campaign.name}
        </h3>

        <div className="flex flex-wrap gap-6 sm:gap-10">
          {campaign.campaign.project_code ? (
            <div className="flex-1 min-w-[200px]">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Project Number
              </h4>
              <p className="text-gray-900">{campaign.campaign.project_code}</p>
            </div>
          ) : null}

          {campaign.campaign.budget ? (
            <div className="flex-1 min-w-[200px]">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Total Budget
              </h4>
              <p className="text-gray-900">${campaign.campaign.budget}</p>
            </div>
          ) : null}

          <div className="flex-1 min-w-[200px]">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Languages
            </h4>
            <p className="text-gray-900 break-words">
              {campaign.languages.map((lang) => lang.name).join(", ")}
            </p>
          </div>

          <div className="flex-1 min-w-[200px]">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Team Member
            </h4>
            <div className="flex flex-wrap gap-2">
              {campaign.users.map((member, index) => {
                if (member.profile_picture) {
                  return (
                    <img
                      key={index}
                      src={member.profile_picture}
                      className={`w-8 h-8 rounded-full flex items-center justify-center`}
                    />
                  );
                } else {
                  return (
                    <div
                      key={index}
                      className={`w-8 h-8 rounded-full ${getColorByHash(
                        member.first_name,
                      )} flex items-center justify-center text-white text-xs font-medium`}
                    >
                      {member.first_name.slice(0, 1).toUpperCase() +
                        member.last_name.slice(0, 1).toUpperCase()}
                    </div>
                  );
                }
              })}
            </div>
          </div>
        </div>
      </div>

      <hr className="mt-2 border-[#EA33C0] mb-8" />

      {children}
    </div>
  );
};

export default CampaignDetailWrapper;
